/**
 * Accessibility Utility Functions
 * Common patterns and helpers for WCAG 2.1 AA compliance
 */

/**
 * Handle keyboard events for custom interactive elements
 * Triggers callback on Enter or Space key
 * WCAG 2.1.1 Keyboard (Level A)
 */
export const handleKeyboardActivation = (event, callback) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    callback();
  }
};

/**
 * Generate unique ID for form field associations
 * WCAG 3.3.2 Labels or Instructions (Level A)
 */
let idCounter = 0;
export const generateId = (prefix = 'a11y') => {
  idCounter += 1;
  return `${prefix}-${idCounter}-${Date.now()}`;
};

/**
 * Create accessible error message attributes
 * Returns props to spread on input element
 * WCAG 3.3.1 Error Identification (Level A)
 */
export const getErrorProps = (error, errorId) => {
  if (!error) return {};
  
  return {
    'aria-invalid': true,
    'aria-describedby': errorId
  };
};

/**
 * Focus management - trap focus within element
 * Used for modals, dialogs, dropdown menus
 * WCAG 2.4.3 Focus Order (Level A)
 */
export const createFocusTrap = (element) => {
  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');

  const getFocusableElements = () => {
    return Array.from(element.querySelectorAll(focusableSelectors));
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Tab') return;

    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Shift + Tab (backwards)
    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    }
    // Tab (forwards)
    else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };

  element.addEventListener('keydown', handleKeyDown);

  // Return cleanup function
  return () => {
    element.removeEventListener('keydown', handleKeyDown);
  };
};

/**
 * Announce content to screen readers
 * WCAG 4.1.3 Status Messages (Level AA)
 */
export const announce = (message, priority = 'polite') => {
  const liveRegion = document.getElementById('a11y-announcer') || createLiveRegion();
  liveRegion.setAttribute('aria-live', priority);
  liveRegion.textContent = message;
  
  // Clear after announcement
  setTimeout(() => {
    liveRegion.textContent = '';
  }, 1000);
};

const createLiveRegion = () => {
  const region = document.createElement('div');
  region.id = 'a11y-announcer';
  region.className = 'sr-only';
  region.setAttribute('role', 'status');
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('aria-atomic', 'true');
  document.body.appendChild(region);
  return region;
};

/**
 * Check if element is keyboard focusable
 */
export const isFocusable = (element) => {
  if (!element) return false;
  
  const tabindex = element.getAttribute('tabindex');
  if (tabindex && parseInt(tabindex) < 0) return false;
  
  if (element.disabled) return false;
  
  const focusableTags = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'];
  return focusableTags.includes(element.tagName) || tabindex !== null;
};

/**
 * Get accessible name for element
 * Useful for debugging and testing
 */
export const getAccessibleName = (element) => {
  // Check aria-label
  if (element.getAttribute('aria-label')) {
    return element.getAttribute('aria-label');
  }
  
  // Check aria-labelledby
  const labelledby = element.getAttribute('aria-labelledby');
  if (labelledby) {
    const labelElement = document.getElementById(labelledby);
    return labelElement ? labelElement.textContent : '';
  }
  
  // Check associated label
  if (element.id) {
    const label = document.querySelector(`label[for="${element.id}"]`);
    if (label) return label.textContent;
  }
  
  // Fallback to text content or alt
  return element.textContent || element.getAttribute('alt') || '';
};

/**
 * Restore focus to element (useful after closing dialogs)
 * WCAG 2.4.3 Focus Order (Level A)
 */
export const restoreFocus = (element) => {
  if (element && isFocusable(element)) {
    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      element.focus();
    }, 0);
  }
};

/**
 * Get contrast ratio between two colors
 * Returns ratio (e.g., 4.5)
 * WCAG 1.4.3 Contrast (Level AA) requires 4.5:1 for normal text
 */
export const getContrastRatio = (foreground, background) => {
  const getLuminance = (hex) => {
    const rgb = parseInt(hex.slice(1), 16);
    const r = ((rgb >> 16) & 0xff) / 255;
    const g = ((rgb >> 8) & 0xff) / 255;
    const b = ((rgb >> 0) & 0xff) / 255;
    
    const [rs, gs, bs] = [r, g, b].map(c => 
      c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    );
    
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  };
  
  const l1 = getLuminance(foreground);
  const l2 = getLuminance(background);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Check if color combination meets WCAG AA standard
 */
export const meetsContrastAA = (foreground, background, isLargeText = false) => {
  const ratio = getContrastRatio(foreground, background);
  const requiredRatio = isLargeText ? 3 : 4.5;
  return ratio >= requiredRatio;
};
